import Link, { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { globalDataStore } from '@/dashboard/store/globalDataStore';
import { SparklesIcon } from '@heroicons/react/outline';
import { Button, GetQuoteButton, JoveLogoGreen } from '@jove-frontend/uikit';
import clsx from 'clsx';
import { useAnalytics } from 'use-analytics';
import { Routes } from '../../utils/routes';
import { QuoteWidget } from '../QuoteWidget';
import PersonalCenterView from './components/PersonalCenter';
import UnreadNotificationView from './components/UnreadNotification';

export const Header = () => {
  const toggleQuoteModal = () => setIsQuoteModalOpen(true);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const {
    state: { queryParams },
  } = globalDataStore();

  useEffect(() => {
    const listener = (e: MessageEvent<string>) => {
      if (e.data === 'close-iframe') {
        setIsQuoteModalOpen(false);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('message', listener);
    }
    return () => window.removeEventListener('message', listener);
  });

  return (
    <header className="header shadow-md">
      <div className="flex justify-between border border-b-black/10 px-10 py-4">
        <div className="flex items-center">
          <JoveLogoGreen className="h-7 w-24" />

          <div className="mx-8 h-full w-px bg-black/30"></div>

          <div className="flex space-x-10">
            <NavButton href={Routes.policies(queryParams?.queryUserId ? { queryUserId: queryParams.queryUserId } : null)} label="My policies" />
            <NavButton href={Routes.documents()} label="Documents" />
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <Button color="plain" size="sm" className="group" onClick={() => (window.location.href = process.env.NX_WEBSITE_COMMUNITY_URL)}>
            <SparklesIcon className="group-hover:text-brand-secondary mr-2 w-5 text-black transition-colors" />
            <span className="group-hover:text-brand-secondary text-black transition-colors">Explore community</span>
          </Button>

          <UnreadNotificationView />
          <PersonalCenterView />

          <GetQuoteButton openQuoteModal={toggleQuoteModal} />
        </div>
      </div>

      <QuoteWidget isOpen={isQuoteModalOpen} />
    </header>
  );
};

type NavButtonProps = LinkProps & {
  label: string;
};

export const NavButton = ({ label, href }: NavButtonProps) => {
  const router = useRouter();
  const { track } = useAnalytics();
  const isActive = router.asPath === href.toString();

  return (
    <Link
      aria-label={label}
      href={href}
      className="numeric-font-fix group flex flex-col items-center text-lg font-semibold transition-colors"
      onClick={() => {
        track('click_nav_link', {
          label,
          href,
        });
      }}
    >
      <span
        className={clsx('transition-color', {
          'text-black group-hover:text-black': isActive,
          'text-black/50 group-hover:text-black/60': !isActive,
        })}
      >
        {label}
      </span>

      <span
        className={clsx('h-2 w-2 rounded-full border border-transparent transition-colors', {
          'bg-brand-secondary': isActive,
          'group-hover:bg-brand-secondary/30': !isActive,
        })}
      ></span>
    </Link>
  );
};
